import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://piznfbmqlygfkrgghupx.supabase.co';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBpem5mYm1xbHlnZmtyZ2dodXB4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODkwNjEyMDcsImV4cCI6MjEwNDYzNzIwN30.Jkp8ky5WYsf5x9JGjTxN4QxroC17nW_Kvu-esRtY2Mk';

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

/**
 * Format raw database row from Supabase into application lead object structure with full safety parsing
 */
export const formatSupabaseLead = (row) => {
  if (!row) return null;

  // 1. Parse formData safely
  let formData = {};
  if (row.form_data) {
    if (typeof row.form_data === 'string') {
      try {
        formData = JSON.parse(row.form_data);
      } catch (e) {
        formData = {};
      }
    } else if (typeof row.form_data === 'object' && row.form_data !== null) {
      formData = row.form_data;
    }
  }

  // Fallback to top-level DB columns if formData is empty or invalid
  if (!formData || typeof formData !== 'object' || Object.keys(formData).length === 0) {
    formData = {
      fullName: row.full_name || '',
      phone: String(row.phone || ''),
      email: row.email || '',
      licenseNumber: row.license_number || '',
      licenseClass: row.license_class || 'Class G',
      provinceOfIssue: row.province_of_issue || row.province || 'Ontario',
      dob: row.dob || '',
      homeAddress: row.home_address || '',
      city: row.city || '',
      province: row.province || 'Ontario',
      country: row.country || 'Canada',
      postalCode: row.postal_code || '',
      emergencyContactName: row.emergency_contact_name || '',
      emergencyContactPhone: row.emergency_contact_phone || '',
      licenseExpiryDate: row.license_expiry_date || '',
      yearsDriving: row.years_driving || '3-5 years',
      endorsements: row.endorsements || '',
      availability: row.availability || 'Full-time',
      earliestStartDate: row.earliest_start_date || '',
      vanDeliveryExperience: row.van_delivery_experience || '',
      consentGiven: row.consent_given ?? true,
      signatureName: row.signature_name || row.full_name || '',
      signatureDate: row.signature_date || ''
    };
  }

  // 2. Parse documents safely
  let documents = {};
  if (row.documents) {
    if (typeof row.documents === 'string') {
      try {
        documents = JSON.parse(row.documents);
      } catch (e) {
        documents = {};
      }
    } else if (typeof row.documents === 'object' && row.documents !== null) {
      documents = row.documents;
    }
  }

  // 3. Parse location safely
  let location = {};
  if (row.location) {
    if (typeof row.location === 'string') {
      try {
        location = JSON.parse(row.location);
      } catch (e) {
        location = {};
      }
    } else if (typeof row.location === 'object' && row.location !== null) {
      location = row.location;
    }
  }

  // 4. Parse comments safely
  let comments = [];
  if (row.comments) {
    if (typeof row.comments === 'string') {
      try {
        comments = JSON.parse(row.comments);
      } catch (e) {
        comments = [];
      }
    } else if (Array.isArray(row.comments)) {
      comments = row.comments;
    }
  }

  return {
    id: String(row.id || ('lead_' + Date.now())),
    referenceNumber: row.reference_number || ('LEAD-2026-' + Math.floor(10000 + Math.random() * 90000)),
    submittedAt: row.submitted_at || row.created_at || new Date().toISOString(),
    status: row.status || 'New Lead',
    company: row.company || 'IRON ROUTE LOGISTICS CORP',
    language: row.language || 'EN',
    formData,
    signatureImage: row.signature_image || null,
    documents,
    location,
    comments
  };
};

/**
 * Fetch all driver recruitment leads from Supabase database
 */
export async function fetchSupabaseLeads() {
  try {
    const { data, error } = await supabase
      .from('driver_recruitment_leads')
      .select('*')
      .order('submitted_at', { ascending: false });

    if (error) {
      console.warn('Supabase fetch error:', error);
      return [];
    }

    return (data || []).map(formatSupabaseLead).filter(Boolean);
  } catch (err) {
    console.error('Supabase fetch error exception:', err);
    return [];
  }
}

/**
 * Insert new candidate lead application into Supabase database
 */
export async function insertSupabaseLead(lead) {
  try {
    const fd = lead.formData || {};

    const dbRow = {
      id: String(lead.id || ('lead_' + Date.now())),
      reference_number: lead.referenceNumber,
      submitted_at: lead.submittedAt || new Date().toISOString(),
      status: lead.status || 'New Lead',
      company: lead.company || 'IRON ROUTE LOGISTICS CORP',
      language: lead.language || 'EN',
      full_name: fd.fullName || 'Applicant',
      dob: fd.dob || null,
      phone: String(fd.phone || ''),
      email: fd.email || '',
      home_address: fd.homeAddress || '',
      city: fd.city || '',
      province: fd.province || 'Ontario',
      country: fd.country || 'Canada',
      postal_code: fd.postalCode || '',
      emergency_contact_name: fd.emergencyContactName || '',
      emergency_contact_phone: fd.emergencyContactPhone || '',
      license_number: fd.licenseNumber || 'Pending Verification',
      license_class: fd.licenseClass || 'Class G',
      province_of_issue: fd.provinceOfIssue || 'Ontario',
      license_expiry_date: fd.licenseExpiryDate || null,
      years_driving: fd.yearsDriving || '3-5 years',
      endorsements: fd.endorsements || '',
      availability: fd.availability || 'Full-time',
      earliest_start_date: fd.earliestStartDate || null,
      van_delivery_experience: fd.vanDeliveryExperience || '',
      consent_given: fd.consentGiven ?? true,
      signature_name: fd.signatureName || fd.fullName || '',
      signature_date: fd.signatureDate || null,
      signature_image: lead.signatureImage || null,
      documents: lead.documents || {},
      location: lead.location || {},
      comments: lead.comments || [],
      form_data: fd
    };

    const { data, error } = await supabase
      .from('driver_recruitment_leads')
      .upsert([dbRow], { onConflict: 'reference_number' })
      .select();

    if (error) {
      console.error('Supabase insert error:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (err) {
    console.error('Supabase insert exception:', err);
    return { success: false, error: err };
  }
}

/**
 * Update candidate lead status and append status audit comment
 */
export async function updateSupabaseLeadStatus(leadId, newStatus, commentObj = null) {
  try {
    const { data: currentLead } = await supabase
      .from('driver_recruitment_leads')
      .select('comments')
      .eq('id', leadId)
      .single();

    const existingComments = currentLead?.comments || [];
    const updatedComments = commentObj ? [...existingComments, commentObj] : existingComments;

    const { data, error } = await supabase
      .from('driver_recruitment_leads')
      .update({
        status: newStatus,
        comments: updatedComments,
        updated_at: new Date().toISOString()
      })
      .eq('id', leadId)
      .select();

    if (error) {
      console.error('Supabase update status error:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (err) {
    console.error('Supabase status update exception:', err);
    return { success: false, error: err };
  }
}

/**
 * Add recruiter comment to candidate application
 */
export async function addSupabaseLeadComment(leadId, commentObj) {
  try {
    const { data: currentLead } = await supabase
      .from('driver_recruitment_leads')
      .select('comments')
      .eq('id', leadId)
      .single();

    const existingComments = currentLead?.comments || [];
    const updatedComments = [...existingComments, commentObj];

    const { data, error } = await supabase
      .from('driver_recruitment_leads')
      .update({
        comments: updatedComments,
        updated_at: new Date().toISOString()
      })
      .eq('id', leadId)
      .select();

    if (error) {
      console.error('Supabase comment add error:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (err) {
    console.error('Supabase add comment exception:', err);
    return { success: false, error: err };
  }
}

/**
 * Delete candidate lead permanently from Supabase
 */
export async function deleteSupabaseLead(leadId) {
  try {
    const { error } = await supabase
      .from('driver_recruitment_leads')
      .delete()
      .eq('id', leadId);

    if (error) {
      console.error('Supabase delete error:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (err) {
    console.error('Supabase delete exception:', err);
    return { success: false, error: err };
  }
}
